/**
 * The staged bed's dress and where its surfaces are — pure numbers shared by
 * the renderer that builds the cloth (`stagingBedroomRenderers.js`) and the
 * planner that seats the kit on it (`../plan/bedroom.js`).
 */

const { beddingLayout } = require('../../dorm-styling/beddingLayout');

const IN = 0.0254;

/**
 * A staged bed's duvet is LOFTIER than a dorm's: a thicker fill, a bigger
 * roll over the mattress edge, deeper folds and a longer drop. White on white
 * shows a duvet only by its shading, so a dorm-weight duvet that reads as
 * cloth under a print reads as a flat slab in plain white.
 *
 * It hangs the SAME drop at the sides and the foot, 15 in from its top: a
 * full/queen duvet is 90 x 90 in over a 60 x 80 in mattress, ~15 in past
 * each side, which reaches the foot of a 12 in mattress under ~4 in of
 * duvet. A shorter foot drop than side drop left
 * the side skirt poking past the foot at each corner, like an untucked sheet,
 * with the bare mattress showing beneath (review, 2026-10-28).
 */
const STAGED_DRESS = Object.freeze({
  duvetThickness: 3.6 * IN,
  roll: 3.2 * IN,
  ripple: 1.1 * IN,
  gather: 1.0 * IN,
  sideDrop: 15 * IN,
  footDrop: 15 * IN,
  // A duvet nobody has touched is too perfect (review, 2026-10-30): soft
  // creases where it was pushed and shallow hollows where it was sat on,
  // spread evenly over the whole top, one per cell of [across, along] grid. The waves keep 35% of `ripple` and
  // the creases rise into the rest, so the top stays where the kit is seated.
  creases: Object.freeze({
    creaseGrid: [4, 4],
    minLength: 8 * IN,
    maxLength: 30 * IN,
    height: 0.75 * IN,
    width: 2.2 * IN,
    foldShare: 0.55, // the fold beside a ridge, as a share of its height
    dentGrid: [2, 3],
    dentRadius: 8 * IN,
    dentDepth: 1.1 * IN,
    waveShare: 0.35,
  }),
  // A grid fine enough for those creases (cloth's default is 11 per metre).
  flatPerM: 55,
  // The fitted sheet rounds the mattress corners, as the mattress does.
  sheetRadius: 1.5 * IN,
});

/**
 * Where a staged bed's surfaces are, for anything seated on it. `topY` is the
 * mattress's top face. The head band (before the fold) is the fitted sheet;
 * the fold is the duvet's head roll with the sheet turned over it; past it,
 * the duvet's top, which ripples by up to `ripple`.
 */
function stagedBedSurfaces(topY) {
  const L = beddingLayout(STAGED_DRESS);
  const sheetTop = topY + L.sheetOver;
  const duvetTop = sheetTop + L.duvetThickness;
  return {
    sheetTop,
    duvetTop,
    turnTop: duvetTop + L.turnThickness,
    duvetPeak: duvetTop + L.ripple,
    foldFromHead: L.headInset,
    turnDepth: L.turnDepth,
    sideDrop: L.sideDrop,
  };
}

module.exports = { STAGED_DRESS, stagedBedSurfaces };
